import { useRef, useState, type MouseEvent as ReactMouseEvent } from 'react';
import { Image } from '@tiptap/extension-image';
import {
  NodeViewWrapper,
  ReactNodeViewRenderer,
  type NodeViewProps,
} from '@tiptap/react';

import { cn } from '@/lib/utils';

const MIN_WIDTH = 80; // px — don't let an image shrink to nothing.

/** Image NodeView with a bottom-right drag handle to resize it in place. The
 *  chosen pixel width is stored on the node (`width`) so it round-trips through
 *  JSON + HTML; `max-w-full` keeps it inside the field regardless. The handle
 *  only shows while editing. */
function ResizableImageView({ node, updateAttributes, editor, selected }: NodeViewProps) {
  const attrs = node.attrs as {
    src: string;
    alt?: string | null;
    title?: string | null;
    width?: number | string | null;
  };
  const wrapRef = useRef<HTMLDivElement>(null);
  const [live, setLive] = useState<number | null>(null);

  const attrWidth =
    typeof attrs.width === 'number'
      ? attrs.width
      : attrs.width
        ? parseInt(String(attrs.width), 10) || null
        : null;
  const displayWidth = live ?? attrWidth;

  function startResize(e: ReactMouseEvent) {
    e.preventDefault();
    const img = wrapRef.current?.querySelector('img');
    const startX = e.clientX;
    const startW = img?.getBoundingClientRect().width ?? 0;
    const maxW = wrapRef.current?.parentElement?.getBoundingClientRect().width ?? startW;
    const clamp = (w: number) => Math.round(Math.max(MIN_WIDTH, Math.min(w, maxW)));

    const onMove = (ev: MouseEvent) => setLive(clamp(startW + ev.clientX - startX));
    const onUp = (ev: MouseEvent) => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
      setLive(null);
      // Commit once on release so the drag is a single undo step.
      updateAttributes({ width: clamp(startW + ev.clientX - startX) });
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
  }

  return (
    <NodeViewWrapper
      ref={wrapRef}
      className="group relative my-2 inline-block max-w-full leading-none"
    >
      <img
        src={attrs.src}
        alt={attrs.alt ?? ''}
        title={attrs.title ?? undefined}
        draggable={false}
        style={{ width: displayWidth ? `${displayWidth}px` : undefined }}
        className={cn('h-auto max-w-full rounded-md', selected && 'ring-primary/60 ring-2')}
      />
      {editor.isEditable ? (
        <span
          role="presentation"
          aria-label="Resize image"
          onMouseDown={startResize}
          className="border-background bg-primary absolute right-1 bottom-1 size-3 cursor-nwse-resize rounded-full border-2 opacity-0 shadow transition-opacity group-hover:opacity-100"
        />
      ) : null}
    </NodeViewWrapper>
  );
}

/** Drop-in replacement for the stock Image node that supports in-field resizing. */
export const ResizableImage = Image.extend({
  addAttributes() {
    return {
      ...this.parent?.(),
      width: {
        default: null,
        parseHTML: (el) => {
          const raw = el.getAttribute('width') || (el as HTMLElement).style?.width || '';
          return raw ? parseInt(raw, 10) || null : null;
        },
        renderHTML: (attributes) =>
          attributes.width ? { width: attributes.width } : {},
      },
    };
  },
  addNodeView() {
    return ReactNodeViewRenderer(ResizableImageView);
  },
});
